import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  // combineLatest,
  concat,
  delay,
  exhaustMap,
  filter,
  forkJoin,
  interval,
  map,
  merge,
  of,
  retry,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { JobStage, JobStatus, JobsApi } from 'src/app/hickory-api';
import { AnalyticsHelper } from 'src/app/shared/analytics/analytics-helper';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { ApiError } from 'src/app/shared/model/utility';

export class AlertPreviewJobHelper implements Disposable {
  private tenantId: number;
  private jobId: string;
  private jobsApi: JobsApi;
  private pollingRateMs = 5000;
  private pollingDelayMs = 10000;
  private stageDelayMs = 500;

  private jobSubject = new BehaviorSubject<JobStatus>(null);
  private stagesSubject = new BehaviorSubject<JobStage[]>(null);
  private progressSubject = new BehaviorSubject<number>(0);
  private stageNumberSubject = new BehaviorSubject<number>(1);
  private destroy$ = new Subject<void>();
  private previousStage = new BehaviorSubject<JobStage>(null);
  private poller$: Observable<unknown>;

  progress$ = this.progressSubject.asObservable();
  stageNumber$ = this.stageNumberSubject.asObservable();

  finished$ = this.jobSubject
    .asObservable()
    .pipe(filter((job) => job && (job.state === 'Success' || job.state === 'Failure')));

  currentStage$: Observable<JobStage | undefined> = this.stagesSubject.pipe(
    switchMap((stages) => {
      if (!stages?.length) {
        return of(undefined);
      }

      const previousStageIndex = stages.findIndex((stage) => stage.id === this.previousStage.value?.id);
      if (previousStageIndex === 0) {
        this.progressSubject.next((stages.length / this.numOfStages) * 100);
        return of(stages[0]);
      }

      // This creates a list of observables delayed by some time.
      // Any subscribers will then receive the batch, each at a delayed interval.
      const stageObservables = stages
        .slice(0, previousStageIndex === -1 ? stages.length : previousStageIndex + 1)
        .reverse()
        .map((stage) =>
          of(stage).pipe(
            tap(() => {
              this.previousStage.next(stage);
              LogHelper.debug('AlertPreviewJobHelper', `Setting current stage to: ${stage.name}`);
            }),
            delay(this.stageDelayMs),
            tap(() => {
              const stageIdx = stages.findIndex((s) => s.id === stage.id);
              this.progressSubject.next(((stages.length - stageIdx) / this.numOfStages) * 100);
              this.stageNumberSubject.next(stages.length - stageIdx);
            }),
          ),
        );

      return concat(...stageObservables);
    }),
  );

  get numOfStages() {
    const stages = this.jobSubject.value?.metadata?.statistics?.['TotalStages'] ?? 0;
    return stages + 1;
  }

  constructor(tenantId: number, jobsApi: JobsApi) {
    this.tenantId = tenantId;
    this.jobsApi = jobsApi;
  }

  [Symbol.dispose](): void {
    this.stopPolling();
  }

  stopPolling(postProcess: Observable<unknown> | null = null) {
    LogHelper.debug('AlertPreviewJobHelper', 'Stopping polling.');
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
    this.poller$ = null;

    if (!postProcess) {
      return;
    }

    // Gets the last set of stages so that we can show them in the UI
    // and then runs any post-proocess steps that we need.
    this.getJobStages().pipe(
      // Delay so that all the stages can be shown
      delay((this.numOfStages - (this.stageNumberSubject.value ?? 0)) * this.stageDelayMs),
      catchError((error) => {
        AnalyticsHelper.logError(error as never, 'AlertPreviewJob.stopPolling');
        return of(null);
      }),
      switchMap(() => postProcess),
    ).subscribe();
  }

  public createJob(): Observable<JobStatus | null> {
    if (!this.tenantId) {
      return of(null);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return this.jobsApi.createJob(this.tenantId, 'AlertPreview').pipe(
      map((response) => {
        return response.item;
      }),
      catchError((error: ApiError) => {
        AnalyticsHelper.logError(error as never, 'AlertPreviewJob.startJob');
        return of(null);
      }),
      tap((job) => {
        this.jobId = job?.id;
        this.jobSubject.next(job);
      }),
    );
  }

  public getPoller(maxPolls: number = Number.POSITIVE_INFINITY) {
    LogHelper.debug('AlertPreviewJobHelper', 'Starting polling');
    if (!this.jobId) {
      LogHelper.debug('AlertPreviewJobHelper', 'Job ID is not set. Polling job cannot be started.');
      return of(null);
    }

    if (!this.tenantId) {
      LogHelper.debug('AlertPreviewJobHelper', 'TenantId not provided for polling. Polling job cannot be started.');
      return of(null);
    }

    const endConditions$ = merge(this.destroy$, this.finished$);
    this.poller$ = interval(this.pollingRateMs).pipe(
      takeUntil(endConditions$),
      take(maxPolls),
      tap(() => {
        LogHelper.debug('AlertPreviewJobHelper', 'Polling job status');
        this.currentStage$.pipe(takeUntil(endConditions$)).subscribe();
      }),
      exhaustMap(() => {
        return forkJoin([this.getJob(), this.getJobStages()]);
      }),
      catchError((error) => {
        AnalyticsHelper.logError(error as never, 'AlertPreviewJob.poller$');
        LogHelper.debug('AlertPreviewJobHelper', 'Polling error. Stopping polling.');
        this.jobSubject.next(null);
        this.stagesSubject.next(null);
        this.stopPolling();
        return of(null);
      }),
    );

    return timer(this.pollingDelayMs - this.pollingRateMs).pipe(switchMap(() => this.poller$ ?? of(null)));
  }

  private getJob(): Observable<JobStatus> {
    if (!this.tenantId || !this.jobId) {
      LogHelper.debug('AlertPreviewJobHelper', 'Job ID is not set. Cannot get job status.');
      return of(null);
    }

    return this.jobsApi.getJobStatuses(this.tenantId, [this.jobId], undefined, 'AlertPreview').pipe(
      retry(2),
      map((response) => {
        return response.items[0];
      }),
      tap((job) => {
        this.jobSubject.next(job);
      }),
    );
  }

  private getJobStages(): Observable<JobStage[]> {
    if (!this.tenantId || !this.jobId) {
      LogHelper.debug('AlertPreviewJobHelper', 'Job ID is not set. Cannot get job stages.');
      return of(null);
    }

    return this.jobsApi.getJobStages(this.tenantId, this.jobId, undefined, undefined, 1, 100, '-timestamp').pipe(
      retry(2),
      map((response) => {
        return response.items;
      }),
      tap((stages) => {
        this.stagesSubject.next(stages);
      }),
    );
  }
}
